import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useVariant } from "@/lib/variant";
import { GammelMark, GammelWords } from "@/components/public/GammelLogo";

/*
  Identity.
  The mark is a "K" drawn as a stem and a chevron, with a small opening between them: the
  schematic shape of a contact, which is what every electrical installation is built from.
  It sits on a square in the brand colour so it reads at 24px and at 240px.
  The wordmark sets the two words in two weights so the lockup has a rhythm without extra graphics.

  On the /gammel variant the lockup is the one from kvinnheradelektro.no instead:
  the lime-green socket mark and "Kvinnherad elektro" in blue.
*/
export const Mark = ({ className = "", title = "Kvinnherad Elektro", inverted = false, light = false, ...rest }) => {
  const glyph = "hsl(var(--ink))";
  return (
    <svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" className={cn("shrink-0", className)} role={title ? "img" : undefined} aria-label={title || undefined} {...rest}>
      <rect width="32" height="32" rx="8" fill="hsl(var(--brand))" />
      <path d="M10.5 8v16" stroke={glyph} strokeWidth="4" strokeLinecap="round" />
      <path d="M22.5 8.5 15.5 16l7 7.5" stroke={glyph} strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  );
};

const Words = ({ light = false, className = "" }) => (
  <span className={cn("font-display leading-none tracking-[-0.02em]", light ? "text-white" : "text-foreground", className)}>
    <span className="font-normal">Kvinnherad</span>{" "}
    <span className="font-semibold">Elektro</span>
  </span>
);

const SIZE = {
  sm: { mark: "h-6 w-6", text: "text-[16px]", gap: "gap-2.5" },
  md: { mark: "h-7 w-7", text: "text-[17px]", gap: "gap-2.5" },
  lg: { mark: "h-9 w-9", text: "text-[21px]", gap: "gap-3" },
};

/* The legacy mark is taller than wide (64x72), so it gets its own sizes and a slightly larger wordmark. */
const GAMMEL_SIZE = {
  sm: { mark: "h-7 w-[25px]", text: "text-[18px]", gap: "gap-2" },
  md: { mark: "h-8 w-[28px]", text: "text-[20px]", gap: "gap-2.5" },
  lg: { mark: "h-10 w-9", text: "text-[25px]", gap: "gap-3" },
};

const Lockup = ({ size, light }) => {
  const variant = useVariant();
  if (variant.id === "gammel") {
    const s = GAMMEL_SIZE[size] || GAMMEL_SIZE.md;
    return (
      <>
        <GammelMark className={cn(s.mark, "text-dot")} title="" />
        <GammelWords light={light} className={s.text} />
      </>
    );
  }
  const s = SIZE[size] || SIZE.md;
  return (
    <>
      <Mark className={s.mark} title="" aria-hidden="true" />
      <Words light={light} className={s.text} />
    </>
  );
};

const gapOf = (variant, size) => ((variant.id === "gammel" ? GAMMEL_SIZE : SIZE)[size] || SIZE.md).gap;

/* Header lockup: mark + wordmark, linked to the front page of the current variant. */
export const Logo = ({ className = "", to, onClick, light = false, suffix, testid = "public-wordmark", ariaLabel = "Kvinnherad Elektro, til framsida", size = "md" }) => {
  const variant = useVariant();
  return (
    <Link to={to || variant.home} onClick={onClick} className={cn("inline-flex items-center rounded-md", gapOf(variant, size), className)} data-testid={testid} aria-label={ariaLabel}>
      <Lockup size={size} light={light} />
      {suffix && <span className={cn("ml-1 font-mono text-[11px] font-medium", light ? "text-white/60" : "text-muted-foreground")}>{suffix}</span>}
    </Link>
  );
};

/* Non-link lockup for the footer and the login page. */
export const Wordmark = ({ className = "", light = false, size = "lg" }) => {
  const variant = useVariant();
  return (
    <div className={cn("inline-flex items-center", gapOf(variant, size), className)}>
      <Lockup size={size} light={light} />
    </div>
  );
};
